import { useState } from "react"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material"
import { useGetIntersectionQuery } from "../../store/chatAnalyzeApi"
import { IntersectionDrawer } from "./IntersectionDrawer"
import type { ChatAnalyze } from "../interfaces"

interface IntersectionSummaryProps {
  selectedChatId: string
  selectedChatName: string
  /** Активный source-фильтр библиотеки (дельта 2026-09-22 §1.1); undefined — параметр не отправляется. */
  source?: string
}

export function IntersectionSummary({
  selectedChatId,
  selectedChatName,
  source,
}: IntersectionSummaryProps) {
  const [drawerChat, setDrawerChat] =
    useState<ChatAnalyze.IntersectionRow | null>(null)

  // Кандидатов выбирает сервер из библиотеки (дельта 2026-09-22, BREAKING):
  // отправляем только chatId базового чата (+ source, если фильтр активен).
  // Аргумент строим без undefined-полей — для явности формы запроса
  // (сериализатор RTK Query и так отбрасывает undefined при формировании ключа кэша).
  const {
    data: rows = [],
    isLoading,
    isError,
    refetch,
  } = useGetIntersectionQuery(
    source ? { chatId: selectedChatId, source } : { chatId: selectedChatId },
  )

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Загрузка пересечений...
        </Typography>
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert
        severity="warning"
        action={
          <Button size="small" onClick={() => refetch()}>
            Повторить
          </Button>
        }
      >
        Не удалось загрузить пересечения
      </Alert>
    )
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Общих участников с другими чатами не найдено.
      </Typography>
    )
  }

  return (
    <>
      <Stack spacing={1}>
        {rows.map((row) => (
          <Stack
            key={row.chatId}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Stack spacing={0.25}>
              <Typography variant="body2" fontWeight={500}>
                {row.chatName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.commonCount.toLocaleString("ru-RU")} общих ·{" "}
                {row.pctOfA.toFixed(1)}% от {selectedChatName} ·{" "}
                {row.pctOfB.toFixed(1)}% от {row.chatName}
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDrawerChat(row)}
            >
              Подробнее
            </Button>
          </Stack>
        ))}
      </Stack>

      {drawerChat && (
        <IntersectionDrawer
          open
          chatA={{ id: selectedChatId, name: selectedChatName }}
          chatB={{ id: drawerChat.chatId, name: drawerChat.chatName }}
          totalCommon={drawerChat.commonCount}
          onClose={() => setDrawerChat(null)}
        />
      )}
    </>
  )
}
